/**
 * Variants and modifiers.
 *
 * Specification: ../test/modifiers.spec.ts. Master plan §4 M1.
 *
 * SIZE IS A VARIANT AXIS, NOT A MODIFIER OPTION.
 *
 * Toast documents that nesting a modifier group underneath a size-pricing
 * modifier does not work — an admission that its two features do not compose.
 * Modelling size as an axis of the item removes the bug class entirely: a
 * modifier nested three levels deep can still price by size, because size is a
 * property of the variant being ordered rather than a sibling choice that may
 * or may not have been made yet.
 *
 * Modifiers are a tree, and are treated as one throughout: an option may open
 * further groups, validation recurses, and pricing flattens the result so a
 * receipt line and a KDS ticket both see every choice with its path intact.
 *
 * Pure functions, no database. This runs at the POS while offline, inside the
 * guest cart validator, and inside the voice loop's latency budget.
 */

// Subpaths, not the kernel barrel: this reaches the till through `./choices`,
// and the barrel carries server code a browser bundle must not.
import { divRound } from "@gf/kernel/money";
import type { TaxCategory } from "@gf/kernel/regulatory";
import { PricingError } from "./pricing-error";
import type { Restriction } from "./availability";

// --- Variants --------------------------------------------------------------

export interface VariantAxis {
  /** e.g. "size", "crust", "milk". */
  readonly key: string;
  /** Ordered — this is the order they are offered in. */
  readonly values: readonly string[];
}

export interface Variant {
  readonly sku: string;
  readonly axisValues: Readonly<Record<string, string>>;
  readonly priceGrossMinor: bigint;
  readonly taxCategory: TaxCategory;
  readonly gtin?: string;
}

export interface VariantMatrix {
  readonly axes: readonly VariantAxis[];
  readonly variants: readonly Variant[];
}

/**
 * The variant for an exact set of axis values, or undefined.
 *
 * Deliberately no fallback. Defaulting to "the first variant" is how a guest
 * is charged for a small and served a large.
 */
export function resolveVariant(
  matrix: VariantMatrix,
  axisValues: Readonly<Record<string, string>>,
): Variant | undefined {
  return matrix.variants.find((v) =>
    matrix.axes.every((axis) => v.axisValues[axis.key] === axisValues[axis.key]),
  );
}

/**
 * Axis combinations with no variant behind them.
 *
 * A gap is legal — there may genuinely be no gluten-free XL. What is not
 * acceptable is discovering it when a guest orders one, so the gaps are
 * reportable and a menu publish can require them to be acknowledged.
 */
export function missingCombinations(
  matrix: VariantMatrix,
): Readonly<Record<string, string>>[] {
  let combos: Record<string, string>[] = [{}];
  for (const axis of matrix.axes) {
    const next: Record<string, string>[] = [];
    for (const combo of combos) {
      for (const value of axis.values) next.push({ ...combo, [axis.key]: value });
    }
    combos = next;
  }
  return combos.filter((c) => resolveVariant(matrix, c) === undefined);
}

// --- Modifier model --------------------------------------------------------

export type Placement = "LEFT" | "RIGHT" | "WHOLE";

/**
 * How a venue prices a half-placed modifier. Both conventions are real, and
 * arguing about which is correct is not our job.
 */
export type PlacementPricing = "PROPORTIONAL" | "FULL";

export type ModifierPriceRule =
  | { readonly kind: "FLAT"; readonly priceGrossMinor: bigint }
  | {
      readonly kind: "BY_SIZE";
      /** Which variant axis to key on. Defaults to "size". */
      readonly axis?: string;
      readonly bySize: Readonly<Record<string, bigint>>;
    }
  | {
      /**
       * Tiered by position within the group: "first topping free, second 25,
       * third and beyond 35". `tiers` covers the leading positions; `beyond`
       * applies to every position after them.
       */
      readonly kind: "BY_SEQUENCE";
      readonly tiers: readonly bigint[];
      readonly beyond: bigint;
    }
  | {
      readonly kind: "BY_SIZE_AND_SEQUENCE";
      readonly axis?: string;
      readonly bySize: Readonly<
        Record<string, { readonly tiers: readonly bigint[]; readonly beyond: bigint }>
      >;
    };

export interface ModifierOption {
  readonly id: string;
  readonly name: string;
  readonly priceRule: ModifierPriceRule;
  /** Default 1. "Triple cheese" is quantity, not three rows. */
  readonly maxQuantity?: number;
  /** Default false. Half-and-half only makes sense for some things. */
  readonly allowsPlacement?: boolean;
  /** Choosing this option opens these groups. Recursion, not a special case. */
  readonly nestedGroups?: readonly ModifierGroup[];
  readonly restrictions?: readonly Restriction[];
}

export interface ModifierGroup {
  readonly id: string;
  readonly name: string;
  /** Above zero makes the group forced. */
  readonly minSelections: number;
  /** null = unlimited. */
  readonly maxSelections: number | null;
  readonly options: readonly ModifierOption[];
  readonly placementPricing?: PlacementPricing;
  readonly restrictions?: readonly Restriction[];
}

export interface ModifierChoice {
  readonly groupId: string;
  readonly optionId: string;
  /** Default 1. */
  readonly quantity?: number;
  /** Default WHOLE. */
  readonly placement?: Placement;
  readonly nested?: readonly ModifierChoice[];
}

// --- Validation ------------------------------------------------------------

export type ModifierViolationKind =
  | "BELOW_MIN"
  | "ABOVE_MAX"
  | "UNKNOWN_GROUP"
  | "UNKNOWN_OPTION"
  | "DUPLICATE_OPTION"
  | "QUANTITY_EXCEEDED"
  | "PLACEMENT_NOT_ALLOWED";

export interface ModifierViolation {
  readonly kind: ModifierViolationKind;
  readonly groupId: string;
  readonly optionId?: string;
  /**
   * Group → option → group → … down to whatever failed.
   *
   * Without it, "you must choose one" is unactionable on a check carrying
   * three steaks.
   */
  readonly path: readonly string[];
  readonly message: string;
}

const quantityOf = (choice: ModifierChoice): number => choice.quantity ?? 1;
const placementOf = (choice: ModifierChoice): Placement => choice.placement ?? "WHOLE";

/**
 * Validate a set of choices against the groups that were actually offered.
 *
 * Recurses into nested groups, and — importantly — rejects nested choices
 * hanging off an option that opens no groups. Otherwise a client could smuggle
 * a priced modifier onto a line by attaching it to a free one.
 */
export function validateModifierChoices(
  groups: readonly ModifierGroup[],
  choices: readonly ModifierChoice[],
  path: readonly string[] = [],
): ModifierViolation[] {
  const violations: ModifierViolation[] = [];
  const byId = new Map(groups.map((g) => [g.id, g]));

  for (const choice of choices) {
    if (!byId.has(choice.groupId)) {
      violations.push({
        kind: "UNKNOWN_GROUP",
        groupId: choice.groupId,
        optionId: choice.optionId,
        path: [...path, choice.groupId],
        message:
          `group "${choice.groupId}" was not offered here. Choices may only answer groups ` +
          `the catalogue actually presented — anything else is a client constructing its own menu.`,
      });
    }
  }

  for (const group of groups) {
    const groupPath = [...path, group.id];
    const chosen = choices.filter((c) => c.groupId === group.id);

    if (chosen.length < group.minSelections) {
      violations.push({
        kind: "BELOW_MIN",
        groupId: group.id,
        path: groupPath,
        message:
          `"${group.name}" requires at least ${group.minSelections} selection(s); ` +
          `${chosen.length} made.`,
      });
    }
    if (group.maxSelections !== null && chosen.length > group.maxSelections) {
      violations.push({
        kind: "ABOVE_MAX",
        groupId: group.id,
        path: groupPath,
        message:
          `"${group.name}" allows at most ${group.maxSelections} selection(s); ` +
          `${chosen.length} made.`,
      });
    }

    const seen = new Set<string>();
    for (const choice of chosen) {
      const optionPath = [...groupPath, choice.optionId];
      const option = group.options.find((o) => o.id === choice.optionId);

      if (!option) {
        violations.push({
          kind: "UNKNOWN_OPTION",
          groupId: group.id,
          optionId: choice.optionId,
          path: optionPath,
          message: `"${choice.optionId}" is not an option in "${group.name}".`,
        });
        continue;
      }

      if (seen.has(option.id)) {
        violations.push({
          kind: "DUPLICATE_OPTION",
          groupId: group.id,
          optionId: option.id,
          path: optionPath,
          message:
            `"${option.name}" appears twice. Use quantity instead: two rows for one thing ` +
            `means the receipt, the KDS ticket and the stock depletion each have to decide ` +
            `how to collapse them, and they will not agree.`,
        });
      }
      seen.add(option.id);

      const quantity = quantityOf(choice);
      const maxQuantity = option.maxQuantity ?? 1;
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > maxQuantity) {
        violations.push({
          kind: "QUANTITY_EXCEEDED",
          groupId: group.id,
          optionId: option.id,
          path: optionPath,
          message: `"${option.name}" allows a quantity of 1–${maxQuantity}; ${quantity} requested.`,
        });
      }

      if (placementOf(choice) !== "WHOLE" && option.allowsPlacement !== true) {
        violations.push({
          kind: "PLACEMENT_NOT_ALLOWED",
          groupId: group.id,
          optionId: option.id,
          path: optionPath,
          message: `"${option.name}" cannot be placed on half.`,
        });
      }

      violations.push(
        ...validateModifierChoices(option.nestedGroups ?? [], choice.nested ?? [], optionPath),
      );
    }
  }

  return violations;
}

// --- Pricing ---------------------------------------------------------------

export interface PricedModifierChoice {
  readonly groupId: string;
  readonly optionId: string;
  readonly name: string;
  readonly quantity: number;
  readonly placement: Placement;
  readonly priceDeltaGrossMinor: bigint;
  /** group → option → … , so a KDS ticket can render the tree it came from. */
  readonly path: readonly string[];
}

function axisValue(variant: Variant, axis: string, optionName: string): string {
  const value = variant.axisValues[axis];
  if (value === undefined) {
    throw new PricingError(
      `"${optionName}" prices by the "${axis}" axis, but variant ${variant.sku} has no such ` +
        `axis. Either the price rule names the wrong axis or the matrix is missing one.`,
    );
  }
  return value;
}

function required<T>(table: Readonly<Record<string, T>>, key: string, optionName: string): T {
  const entry = table[key];
  if (entry === undefined) {
    throw new PricingError(
      `"${optionName}" has no price for "${key}". A missing modifier price must not silently ` +
        `become zero — that is a revenue leak on every sale of this combination.`,
    );
  }
  return entry;
}

/** Price for one position in a tiered sequence. */
const tierPrice = (tiers: readonly bigint[], beyond: bigint, slot: number): bigint =>
  tiers[slot] ?? beyond;

/**
 * Price a set of choices against the variant being ordered.
 *
 * Returns a flat list in the order the choices were made, nested ones included,
 * each carrying its path. Flat because that is what a receipt line, a KDS
 * ticket and a stock depletion all need; the path preserves the tree for
 * anything that wants to render it.
 *
 * The sequence counter is per group and shared across its options: "first
 * topping free" means the first topping, not the first of each kind.
 */
export function priceModifiers(
  groups: readonly ModifierGroup[],
  choices: readonly ModifierChoice[],
  variant: Variant,
  path: readonly string[] = [],
): PricedModifierChoice[] {
  const out: PricedModifierChoice[] = [];
  const byId = new Map(groups.map((g) => [g.id, g]));
  const nextSlot = new Map<string, number>();

  for (const choice of choices) {
    const group = byId.get(choice.groupId);
    if (!group) {
      throw new PricingError(
        `cannot price a choice in group "${choice.groupId}", which was not offered. ` +
          `Run validateModifierChoices first — pricing is not the place to discover this.`,
      );
    }
    const option = group.options.find((o) => o.id === choice.optionId);
    if (!option) {
      throw new PricingError(
        `cannot price "${choice.optionId}", which is not an option in "${group.name}".`,
      );
    }

    const quantity = quantityOf(choice);
    const placement = placementOf(choice);
    const optionPath = [...path, group.id, option.id];
    const slot = nextSlot.get(group.id) ?? 0;

    let amount = 0n;
    switch (option.priceRule.kind) {
      case "FLAT":
        amount = option.priceRule.priceGrossMinor * BigInt(quantity);
        break;
      case "BY_SIZE": {
        const key = axisValue(variant, option.priceRule.axis ?? "size", option.name);
        amount = required(option.priceRule.bySize, key, option.name) * BigInt(quantity);
        break;
      }
      case "BY_SEQUENCE": {
        const { tiers, beyond } = option.priceRule;
        for (let i = 0; i < quantity; i++) amount += tierPrice(tiers, beyond, slot + i);
        break;
      }
      case "BY_SIZE_AND_SEQUENCE": {
        const key = axisValue(variant, option.priceRule.axis ?? "size", option.name);
        const { tiers, beyond } = required(option.priceRule.bySize, key, option.name);
        for (let i = 0; i < quantity; i++) amount += tierPrice(tiers, beyond, slot + i);
        break;
      }
    }

    // Halve once, on the final amount, so a tiered half-portion cannot round
    // twice and drift from what the guest was quoted.
    if (placement !== "WHOLE" && (group.placementPricing ?? "PROPORTIONAL") === "PROPORTIONAL") {
      amount = divRound(amount, 2n);
    }

    // Quantity consumes sequence slots: ordering two hams takes the first and
    // second positions, which is what makes "second one 25" mean anything.
    nextSlot.set(group.id, slot + quantity);

    out.push({
      groupId: group.id,
      optionId: option.id,
      name: option.name,
      quantity,
      placement,
      priceDeltaGrossMinor: amount,
      path: optionPath,
    });

    if (choice.nested && choice.nested.length > 0) {
      out.push(
        ...priceModifiers(option.nestedGroups ?? [], choice.nested, variant, optionPath),
      );
    }
  }

  return out;
}
